import { get } from './apiClient';

/**
 * A supplier's statement of account (D49 phase 3): one supplier, one currency, a date range.
 * Opening balance, every invoice, payment and credit in date order with a running balance, then
 * the closing balance. Everything is computed by the server; nothing here re-adds a figure.
 *
 * `debit` is money we owe going UP (an invoice, a credit taken back), `credit` is it going DOWN
 * (a payment, a credit, money marked paid). `balance` is what we owe after the line; negative
 * means the supplier owes us.
 */

export type StatementLineType = 'invoice' | 'payment' | 'credit' | 'credit_reversal' | 'adjustment';

export interface StatementLine {
  /** A calendar day, `yyyy-mm-dd`. */
  date: string;
  type: StatementLineType;
  reference: string;
  description: string;
  debit: number;
  credit: number;
  balance: number;
  /** The bill this line is about; null for a payment that settles several. */
  billId: string | null;
  /** The payment voucher; only on a payment line. */
  paymentId: string | null;
  /** True when the date is not the document's own (it came from when the record was made). */
  dateEstimated: boolean;
}

export interface StatementReconciliationDifference {
  billId: string;
  invoiceNumber: string;
  /** What the statement's own lines add up to for this bill. */
  statement: number;
  /** What the bill itself stores as outstanding. */
  stored: number;
}

export interface StatementReconciliation {
  /** False when the statement ends before today: the stored balances are today's. */
  checked: boolean;
  currentOutstanding: number | null;
  matches: boolean | null;
  differences: StatementReconciliationDifference[];
}

export interface StatementTotals {
  invoiced: number;
  paid: number;
  credited: number;
  adjustments: number;
}

export interface SupplierStatement {
  supplierName: string;
  /** Null only while `currencyRequired` is true. */
  currency: string | null;
  currencies: string[];
  /** The supplier has bills in several currencies and none was chosen: nothing is added up. */
  currencyRequired: boolean;
  from: string;
  to: string;
  opening: number | null;
  lines: StatementLine[];
  closing: number | null;
  totals: StatementTotals | null;
  reconciliation: StatementReconciliation | null;
  generatedAt: string;
}

export interface SupplierStatementQuery {
  supplier: string;
  currency?: string;
  /** `yyyy-mm-dd`. Blank: the server uses 90 days before `to`. */
  from?: string;
  /** `yyyy-mm-dd`. Blank: today. */
  to?: string;
}

export async function getSupplierStatement(query: SupplierStatementQuery): Promise<SupplierStatement> {
  const qs = new URLSearchParams();
  qs.set('supplier', query.supplier);
  if (query.currency) qs.set('currency', query.currency);
  if (query.from) qs.set('from', query.from);
  if (query.to) qs.set('to', query.to);
  const res = await get<{ success: boolean; data: SupplierStatement }>(`/supplier-statements?${qs.toString()}`);
  return res.data;
}
